// API client facade - re-exports all API modules for convenient access
import { alarmsApi } from "./schedules-api";
import { camerasApi, type CameraApiResponse, type LivestreamStartResponse } from "./cameras-api";
import { agentApi } from "./agent-api";
import { activityApi, type ActivityListParams, type ActivityListResponse } from "./activity-api";
import { contactsApi } from "./contacts-api";
import type {
  AgentRequest,
  AgentResponse,
  ArmedResponse,
  AlarmRoutine,
  AlarmException,
  AlarmOverride,
  RoutineSuggestion,
  AlarmExceptionSuggestion,
} from "./types";
import { AlarmConfiguration, AlarmContacts } from "@visioni/a3s-alarm-schedules/schema";

export const api = {
  alarms: {
    list: (tenantId: string, siteId: string): Promise<AlarmConfiguration[]> => alarmsApi.list(tenantId, siteId),

    getByCamera: (tenantId: string, siteId: string, cameraKey: string): Promise<AlarmConfiguration> =>
      alarmsApi.getByCamera(tenantId, siteId, cameraKey),

    updateResponse: (
      tenantId: string,
      siteId: string,
      cameraKey: string,
      response: ArmedResponse,
    ): Promise<AlarmConfiguration> => alarmsApi.updateResponse(tenantId, siteId, cameraKey, response),

    updateRoutine: (
      tenantId: string,
      siteId: string,
      cameraKey: string,
      routine: AlarmRoutine,
      timezone: string,
    ): Promise<AlarmConfiguration> => alarmsApi.updateRoutine(tenantId, siteId, cameraKey, routine, timezone),

    updateExceptions: (
      tenantId: string,
      siteId: string,
      cameraKey: string,
      exceptions: AlarmException[],
      timezone: string,
    ): Promise<AlarmConfiguration> => alarmsApi.updateExceptions(tenantId, siteId, cameraKey, exceptions, timezone),

    updateOverride: (
      tenantId: string,
      siteId: string,
      cameraKey: string,
      override: AlarmOverride | null,
      timezone: string,
    ): Promise<AlarmConfiguration> => alarmsApi.updateOverride(tenantId, siteId, cameraKey, override, timezone),

    updateTimezone: (
      tenantId: string,
      siteId: string,
      cameraKey: string,
      timezone: string,
    ): Promise<AlarmConfiguration> => alarmsApi.updateTimezone(tenantId, siteId, cameraKey, timezone),

    updateSettings: (
      tenantId: string,
      siteId: string,
      cameraKey: string,
      body: {
        response?: ArmedResponse;
        routine?: AlarmRoutine;
        exceptions?: AlarmException[];
        timezone?: string;
      },
    ): Promise<AlarmConfiguration> => alarmsApi.updateSettings(tenantId, siteId, cameraKey, body),

    getRoutineSuggestions: (tenantId: string, siteId: string, cameraKey: string): Promise<RoutineSuggestion[]> =>
      alarmsApi.getRoutineSuggestions(tenantId, siteId, cameraKey),

    getExceptionSuggestions: (
      tenantId: string,
      siteId: string,
      cameraKey: string,
    ): Promise<AlarmExceptionSuggestion[]> => alarmsApi.getExceptionSuggestions(tenantId, siteId, cameraKey),

    updateSilence: (
      tenantId: string,
      siteId: string,
      cameraKey: string,
      silence: { id: string; reason?: string; period: { start: unknown; end: unknown } } | null,
      timezone: string,
    ): Promise<AlarmConfiguration> => camerasApi.updateSilence(tenantId, siteId, cameraKey, silence, timezone),

    agent: {
      query: (tenantId: string, siteId: string, cameraKey: string, request: AgentRequest): Promise<AgentResponse> =>
        agentApi.query(tenantId, siteId, cameraKey, request),
    },
  },

  cameras: {
    list: (tenantId: string, siteId: string): Promise<CameraApiResponse[]> => camerasApi.list(tenantId, siteId),

    search: (tenantId: string): Promise<CameraApiResponse[]> => camerasApi.search(tenantId),

    get: (tenantId: string, siteId: string, cameraKey: string): Promise<CameraApiResponse> =>
      camerasApi.get(tenantId, siteId, cameraKey),

    startLivestreamSession: (sessionStartPath: string): Promise<LivestreamStartResponse> =>
      camerasApi.startLivestreamSession(sessionStartPath),

    updateNickname: (
      tenantId: string,
      siteId: string,
      cameraKey: string,
      nickname: string | null,
    ): Promise<{ nickname: string | null }> => camerasApi.updateNickname(tenantId, siteId, cameraKey, nickname),

    getJpegStreamUrl: (tenantId: string, siteId: string, cameraKey: string): Promise<string | null> =>
      camerasApi.getJpegStreamUrl(tenantId, siteId, cameraKey),
  },

  activity: {
    list: (tenantId: string, params: ActivityListParams = {}): Promise<ActivityListResponse> =>
      activityApi.list(tenantId, params),
  },

  contacts: {
    getCameraContacts: (tenantId: string, siteId: string, cameraKey: string) =>
      contactsApi.getCameraContacts(tenantId, siteId, cameraKey),

    updateCameraContacts: (tenantId: string, siteId: string, cameraKey: string, contacts: AlarmContacts) =>
      contactsApi.updateCameraContacts(
        tenantId,
        siteId,
        cameraKey,
        contacts ?? {
          sms_contacts: [],
          email_contacts: [],
        },
      ),

    getEmailSuggestions: (tenantId: string, siteId: string, cameraKey: string, query?: string) =>
      contactsApi.getEmailSuggestions(tenantId, siteId, cameraKey, query),
  },
};
